/**
 * @file BillingDetailsTab.js
 * @description Tab component for managing client billing details and SMS allocation settings.
 */

import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import Toast from '../../ui/toasts/Toast';
import {
    createBillingSettings,
    updateBillingSettings,
    getSmsBalance,
} from '../../../services/clientService';
import { tenantTierAllocations } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

const BillingDetailsTab = forwardRef(
    (
        {
            data,
            clientId,
            billingSettings,
            isCreateMode,
            onRefresh,
            highlightedField,
            onFieldFocus,
            tenantTierId,
        },
        ref,
    ) => {
        const getDefaultValues = tierId => {
            const tierAllocation = tenantTierAllocations.find(
                tier => tier.id === tierId,
            );
            const incrementalAllocation = tenantTierAllocations.find(
                tier => tier.id === null,
            );

            if (tierAllocation) {
                // Use tier's monthly allocation, but check if incremental values need fallback
                const shouldUseIncrementalFallback =
                    (tierAllocation.incrementalSMSAllocationAmount === 0 ||
                        tierAllocation.incrementalSMSAllocationAmount ===
                            null) &&
                    (tierAllocation.incrementalSMSAllocationCost === 0 ||
                        tierAllocation.incrementalSMSAllocationCost === null);

                return {
                    monthlySMSAllocationAmount:
                        tierAllocation.monthlySMSAllocationAmount,
                    incrementalSMSAllocationAmount:
                        shouldUseIncrementalFallback && incrementalAllocation
                            ? incrementalAllocation.incrementalSMSAllocationAmount
                            : tierAllocation.incrementalSMSAllocationAmount,
                    incrementalSMSAllocationCost:
                        shouldUseIncrementalFallback && incrementalAllocation
                            ? incrementalAllocation.incrementalSMSAllocationCost
                            : tierAllocation.incrementalSMSAllocationCost,
                };
            }
            // Fallback to default values if tier not found
            return {
                monthlySMSAllocationAmount: 0,
                incrementalSMSAllocationAmount: 0,
                incrementalSMSAllocationCost: 0,
            };
        };

        const [smsBalance, setSmsBalance] = useState(null);
        const [formData, setFormData] = useState(() => {
            if (billingSettings) {
                return {
                    tenantId: data.id || 0,
                    monthlySMSAllocationAmount:
                        billingSettings.monthlySMSAllocationAmount,
                    incrementalSMSAllocationAmount:
                        billingSettings.incrementalSMSAllocationAmount,
                    incrementalSMSAllocationCost:
                        billingSettings.incrementalSMSAllocationCost,
                };
            }

            const defaults = getDefaultValues(tenantTierId);
            return {
                tenantId: data.id || 0,
                monthlySMSAllocationAmount: defaults.monthlySMSAllocationAmount,
                incrementalSMSAllocationAmount:
                    defaults.incrementalSMSAllocationAmount,
                incrementalSMSAllocationCost:
                    defaults.incrementalSMSAllocationCost,
            };
        });

        const [errors, setErrors] = useState({
            monthlySMSAllocationAmount: '',
            incrementalSMSAllocationAmount: '',
            incrementalSMSAllocationCost: '',
        });

        const savedSnapshotRef = useRef(null);

        const isDirty = () =>
            savedSnapshotRef.current !== null &&
            savedSnapshotRef.current !== JSON.stringify(formData);

        useEffect(() => {
            const loadSmsBalance = async () => {
                if (clientId) {
                    try {
                        const balance = await getSmsBalance(clientId);
                        setSmsBalance(balance);
                    } catch (error) {
                        setSmsBalance(null);
                    }
                }
            };

            loadSmsBalance();
        }, [clientId]);

        useEffect(() => {
            let nextFormData;

            if (billingSettings) {
                nextFormData = {
                    tenantId: data.id || 0,
                    monthlySMSAllocationAmount:
                        billingSettings.monthlySMSAllocationAmount,
                    incrementalSMSAllocationAmount:
                        billingSettings.incrementalSMSAllocationAmount,
                    incrementalSMSAllocationCost:
                        billingSettings.incrementalSMSAllocationCost,
                };
            } else {
                const defaults = getDefaultValues(tenantTierId);
                nextFormData = {
                    tenantId: data.id || 0,
                    monthlySMSAllocationAmount:
                        defaults.monthlySMSAllocationAmount,
                    incrementalSMSAllocationAmount:
                        defaults.incrementalSMSAllocationAmount,
                    incrementalSMSAllocationCost:
                        defaults.incrementalSMSAllocationCost,
                };
            }

            setFormData(nextFormData);
            savedSnapshotRef.current = JSON.stringify(nextFormData);
        }, [data, billingSettings, tenantTierId]);

        const isFieldHighlighted = fieldName => {
            return highlightedField === fieldName;
        };

        const getFieldClasses = (fieldName, hasError = false) => {
            let classes = 'form-control';
            if (hasError) {
                classes += ' is-invalid';
            }
            if (isFieldHighlighted(fieldName)) {
                classes += ' is-invalid border-danger';
            }
            return classes;
        };

        const handleFieldFocus = () => {
            if (onFieldFocus) {
                onFieldFocus();
            }
        };

        const validateField = (name, value) => {
            let errorMessage = '';

            switch (name) {
                case 'monthlySMSAllocationAmount':
                    if (value < 0) {
                        errorMessage = getText('msg_a1c73637d4ef');
                    } else if (!Number.isInteger(Number(value))) {
                        errorMessage = getText('msg_68eb3703fd64');
                    }
                    break;

                case 'incrementalSMSAllocationAmount':
                    if (value < 0) {
                        errorMessage = getText('msg_13a3f5fd551a');
                    } else if (!Number.isInteger(Number(value))) {
                        errorMessage = getText('msg_873f34376a7f');
                    }
                    break;

                case 'incrementalSMSAllocationCost':
                    if (value < 0) {
                        errorMessage = getText('msg_684223202e7e');
                    }
                    break;

                default:
                    break;
            }

            return errorMessage;
        };

        const handleChange = e => {
            const { name, value } = e.target;

            let fieldValue = value;

            if (value !== '') {
                if (
                    name === 'monthlySMSAllocationAmount' ||
                    name === 'incrementalSMSAllocationAmount'
                ) {
                    const parsed = parseInt(value);
                    fieldValue = isNaN(parsed) ? 0 : parsed;
                } else if (name === 'incrementalSMSAllocationCost') {
                    const parsed = parseFloat(value);
                    fieldValue = isNaN(parsed) ? 0 : parsed;
                }
            }

            const validationValue = value === '' ? 0 : fieldValue;
            const errorMessage = validateField(name, validationValue);

            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: errorMessage,
            }));

            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    [name]: fieldValue,
                };

                if (
                    name === 'incrementalSMSAllocationAmount' &&
                    (fieldValue === 0 || fieldValue === '')
                ) {
                    newData.incrementalSMSAllocationCost = 0;
                    setErrors(prev => ({
                        ...prev,
                        incrementalSMSAllocationCost: '',
                    }));
                }

                return newData;
            });
        };

        const getProcessedFormData = () => ({
            ...formData,
            monthlySMSAllocationAmount:
                formData.monthlySMSAllocationAmount === ''
                    ? 0
                    : Number(formData.monthlySMSAllocationAmount),
            incrementalSMSAllocationAmount:
                formData.incrementalSMSAllocationAmount === ''
                    ? 0
                    : Number(formData.incrementalSMSAllocationAmount),
            incrementalSMSAllocationCost:
                formData.incrementalSMSAllocationCost === ''
                    ? 0
                    : Number(formData.incrementalSMSAllocationCost),
        });

        const validate = () => {
            const processedFormData = getProcessedFormData();
            const validationErrors = {};
            Object.keys(processedFormData).forEach(key => {
                if (key !== 'tenantId') {
                    const error = validateField(key, processedFormData[key]);
                    if (error) {
                        validationErrors[key] = error;
                    }
                }
            });

            setErrors(validationErrors);

            const isValid = Object.keys(validationErrors).length === 0;
            if (!isValid) {
                Toast.error(getText('msg_6d7767e3fa16'));
            }

            return isValid;
        };

        const saveBillingSettings = async () => {
            if (!validate()) {
                return false;
            }

            const processedFormData = getProcessedFormData();

            try {
                if (isCreateMode) {
                    await createBillingSettings(processedFormData, data.id);
                } else {
                    await updateBillingSettings(processedFormData, data.id);
                }

                savedSnapshotRef.current = JSON.stringify(formData);

                if (onRefresh) {
                    onRefresh();
                }

                return true;
            } catch (error) {
                console.error('Failed to save billing settings', error);
                const errorMessage =
                    error.response?.data?.detail || getText('msg_f5be0cd76eff');
                Toast.error(errorMessage);
                return false;
            }
        };

        useImperativeHandle(ref, () => ({
            save: saveBillingSettings,
            isDirty,
            validate,
        }));

        return (
            <div className="col-12 col-xs-12 col-md-8 col-lg-8 m-auto pb-5 pt-4">
                <div className="row">
                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="smsBalance">
                                {getText('msg_0979ab72ab74')}
                            </label>
                            <div className="position-relative mx-2">
                                {smsBalance?.balance?.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-12 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="monthlySMSAllocationAmount">
                                {getText('msg_7da817dc3a93')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <input
                                    type="number"
                                    id="monthlySMSAllocationAmount"
                                    name="monthlySMSAllocationAmount"
                                    className={getFieldClasses(
                                        'monthlySMSAllocationAmount',
                                        errors.monthlySMSAllocationAmount,
                                    )}
                                    value={formData.monthlySMSAllocationAmount}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    required
                                    placeholder={getText('msg_b2b675a587fe')}
                                    min="0"
                                    step="1"
                                    tabIndex={1}
                                />
                                {errors.monthlySMSAllocationAmount ? (
                                    <div className="invalid-feedback">
                                        {errors.monthlySMSAllocationAmount}
                                    </div>
                                ) : (
                                    <small className="text-muted">
                                        {getText('msg_42a5c70f21ad')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="incrementalSMSAllocationAmount">
                                {getText('msg_3280d13dc383')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <input
                                    type="number"
                                    id="incrementalSMSAllocationAmount"
                                    name="incrementalSMSAllocationAmount"
                                    className={getFieldClasses(
                                        'incrementalSMSAllocationAmount',
                                        errors.incrementalSMSAllocationAmount,
                                    )}
                                    value={
                                        formData.incrementalSMSAllocationAmount
                                    }
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    required
                                    placeholder={getText('msg_95160120d430')}
                                    min="0"
                                    step="1"
                                    tabIndex={2}
                                />
                                {errors.incrementalSMSAllocationAmount ? (
                                    <div className="invalid-feedback">
                                        {errors.incrementalSMSAllocationAmount}
                                    </div>
                                ) : (
                                    <small className="text-muted">
                                        {getText('msg_42a5c70f21ad')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="incrementalSMSAllocationCost">
                                {getText('msg_8915e08a8752')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <div className="input-group">
                                    <input
                                        type="number"
                                        id="incrementalSMSAllocationCost"
                                        name="incrementalSMSAllocationCost"
                                        className={getFieldClasses(
                                            'incrementalSMSAllocationCost',
                                            errors.incrementalSMSAllocationCost,
                                        )}
                                        value={
                                            formData.incrementalSMSAllocationCost
                                        }
                                        onChange={handleChange}
                                        onFocus={handleFieldFocus}
                                        required
                                        placeholder={getText(
                                            'msg_22cf82b68b95',
                                        )}
                                        min="0"
                                        step="0.01"
                                        tabIndex={3}
                                        disabled={
                                            formData.incrementalSMSAllocationAmount ===
                                                0 ||
                                            formData.incrementalSMSAllocationAmount ===
                                                ''
                                        }
                                    />
                                </div>
                                {errors.incrementalSMSAllocationCost && (
                                    <div className="invalid-feedback">
                                        {errors.incrementalSMSAllocationCost}
                                    </div>
                                )}
                                {formData.incrementalSMSAllocationAmount ===
                                    0 ||
                                formData.incrementalSMSAllocationAmount ===
                                    '' ? (
                                    <small className="text-muted">
                                        {getText('msg_67fa0a6786f1')}
                                    </small>
                                ) : (
                                    <small className="text-muted">
                                        {getText('msg_42a5c70f21ad')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 pt-4 col-12 m-auto">
                    <p>
                        <span className="text-danger">*</span>{' '}
                        {getText('msg_7c118f042eaa')}
                    </p>
                </div>
            </div>
        );
    },
);

BillingDetailsTab.displayName = 'BillingDetailsTab';

export default BillingDetailsTab;
